import {
  buildCursorSvg,
  buildHighlightCss,
  buildKeycapCss,
  CURSOR_ELEMENT_ID,
  CURSOR_HOST_TAG,
} from './cursor-visuals.js';

/**
 * The in-page cue runtime: a self-contained script that installs
 * `window.__chatsdetCue` and owns EVERY pixel the recorded-video overlay draws.
 *
 * Two callers drive it:
 *  - the reactive listeners (in this same script) animate on the real DOM events
 *    Playwright dispatches — that is the "it actually happened" signal;
 *  - the driver calls `cue(...)` from {@link AutonomousCursor} BEFORE dispatching
 *    an action — that is the "this is about to happen" signal.
 *
 * **Exactly one renderer, always the top frame.** `addInitScript` runs this in
 * EVERY frame, child frames included. A child frame that rendered its own cursor
 * would produce a second arrow positioned in frame-relative coordinates, while a
 * child on an opaque origin (a `srcdoc` iframe, where `sessionStorage` throws)
 * would render nothing at all and the top document would never see its input —
 * both verified in a real browser. So child frames install a FORWARDER ONLY:
 * they post real input events up to their parent, each hop translating
 * coordinates by the child iframe's own bounding rect, until the top frame — the
 * only frame that ever creates a host — renders them in main-frame coordinates.
 * This also matches the driver side, which resolves boxes with
 * `Locator.boundingBox()` (already main-frame relative).
 *
 * **Why a closed shadow root.** Every node this runtime creates lives inside a
 * closed shadow root on a single {@link CURSOR_HOST_TAG} host:
 *  1. `getByText`, broad CSS selectors, and accessibility snapshots cannot see
 *     overlay CONTENT — critical now that keycaps render real text like "Enter".
 *  2. Document-level `MutationObserver`s do not observe mutations inside an
 *     unobserved shadow root, so per-action animation does not churn
 *     `__domRevision` (see `playwright-client/dom-revision-script.ts`).
 *  3. Page CSS cannot reach in and restyle us, and our CSS cannot leak out.
 *
 * The host is NOT invisible and this is deliberately not overclaimed: it is still
 * one element in `body.children`, still matches `body > :last-child` and
 * `querySelectorAll('*')`, and page JavaScript can find or remove it. The
 * mitigation is to install it ONCE per document and never remove/reinsert it.
 *
 * **The API is locked twice over.** `window.__chatsdetCue` is defined
 * non-writable and non-configurable and the object is frozen — that stops a page
 * REPLACING `cue()`, which matters because the driver sleeps for a duration this
 * API returns. Freezing alone would still leave it publicly *callable*, and a
 * page that can call `cue({ gen: HUGE })` advances the generation and silently
 * invalidates every legitimate cue for the rest of the block. So every method
 * also requires a per-install capability token that lives only in this closure
 * and in the driver; init-script source is not readable from the page. The driver
 * independently validates and clamps whatever comes back — neither side trusts
 * the other alone.
 *
 * **Generations.** Every `run_code` block bumps a generation. A cue carrying a
 * generation OLDER than the current one is rejected as stale; a NEWER one is
 * adopted. Adoption is what makes navigation work: `addInitScript` re-runs on the
 * new document with `generation` back at 0, and without adoption every subsequent
 * cue in that block would be silently dropped.
 *
 * Emitted as a pure string with no external references. Nothing caller-supplied
 * flows into the markup or CSS (colors are baked constants; key labels are
 * assigned via `textContent`, never as HTML), so there is no injection surface.
 *
 * @module
 */

/** sessionStorage key gating whether the reactive listeners animate. */
export const ACTIVE_KEY = '__chatsdetAutonomousActive';

/** Global the runtime installs itself on. The driver calls through this. */
export const CUE_GLOBAL = '__chatsdetCue';

/** postMessage discriminator for child-frame input forwarding. */
export const INPUT_MESSAGE_KEY = '__chatsdetCursorInput';

/** How long the cursor arrow's CSS transition runs when no glide is computed. */
const DEFAULT_TRANSITION_MS = 120;

/** Cursor glide speed and bounds — mirrors {@link CursorOverlay}'s pacing. */
const GLIDE_PX_PER_MS = 1.5;
const GLIDE_MIN_MS = 120;
export const GLIDE_MAX_MS = 600;

/**
 * Build the in-page runtime script.
 *
 * Idempotent per document via `window.__chatsdetCursorAutonomousInstalled`, so a
 * re-`evaluate` on an already-initialized document is a no-op.
 */
export function buildCursorRuntimeScript(token: string): string {
  const svg = buildCursorSvg();
  const css = buildHighlightCss() + buildKeycapCss();
  return `(() => {
  if (window.__chatsdetCursorAutonomousInstalled) return;
  window.__chatsdetCursorAutonomousInstalled = true;

  var HOST_TAG = ${JSON.stringify(CURSOR_HOST_TAG)};
  var CURSOR_ID = ${JSON.stringify(CURSOR_ELEMENT_ID)};
  var ACTIVE_KEY = ${JSON.stringify(ACTIVE_KEY)};
  var MSG = ${JSON.stringify(INPUT_MESSAGE_KEY)};
  var SVG = ${JSON.stringify(svg)};
  var CSS = ${JSON.stringify(css)};
  var TOKEN = ${JSON.stringify(token)};
  var POS_KEY = '__chatsdetCursorPos';
  /** Finite-number guard for every value crossing an untrusted boundary. */
  function num(v) { return typeof v === 'number' && isFinite(v); }
  var GLIDE_PX_PER_MS = ${GLIDE_PX_PER_MS};
  var GLIDE_MIN_MS = ${GLIDE_MIN_MS};
  var GLIDE_MAX_MS = ${GLIDE_MAX_MS};
  var DEFAULT_TRANSITION_MS = ${DEFAULT_TRANSITION_MS};

  var isTop = (function () { try { return window.top === window; } catch (e) { return false; } })();

  /**
   * Affine mapping from the child document hosted by \`win\` into OUR viewport,
   * or null if that window is not one of our own frames.
   *
   * Scale matters, not just offset: an iframe under transform: scale() or CSS
   * zoom renders its child at a different size than the child's own layout
   * coordinates, so adding raw clientX/clientY to the rect origin would land the
   * cursor in the wrong place. getBoundingClientRect() reports the TRANSFORMED
   * border box and offsetWidth/Height the UNTRANSFORMED border box, so their
   * ratio is the scale — and it is exactly 1 for an ordinary iframe, borders
   * included. (clientWidth excludes borders, which made a plain 2px-bordered
   * iframe scale by 404/400.) Rotation and skew cannot be expressed this way, so
   * such frames are SUPPRESSED rather than mapped wrongly — no ripple beats a
   * ripple pointing where the user never clicked.
   */
  /**
   * Whether an element and its ancestors carry only axis-aligned transforms.
   *
   * Rotation and skew cannot be expressed by the scale+offset mapping below, so a
   * forwarded coordinate from such a frame would render the cursor at a
   * confidently wrong position. We suppress those frames instead: no ripple is
   * better than one pointing somewhere the user never clicked.
   */
  function axisAligned(el) {
    var node = el, hops = 0;
    while (node && node.nodeType === 1) {
      // Fail CLOSED if the walk is truncated: a rotation above the cap would
      // otherwise be mapped rather than suppressed.
      if (hops++ >= 64) return false;
      var cs = null;
      try { cs = getComputedStyle(node); } catch (e) { return false; }
      if (!cs) return false;
      var t = cs.transform;
      if (t && t !== 'none') {
        try {
          var m = new DOMMatrixReadOnly(t);
          // b/c catch rotation and skew in the 2D plane...
          if (Math.abs(m.b) > 1e-6 || Math.abs(m.c) > 1e-6) return false;
          // ...but rotate(180deg) and scaleX(-1) leave b = c = 0 and flip the
          // axis signs, which the positive rect/offset ratio below cannot see.
          if (m.a < 0 || m.d < 0) return false;
          // A 3D matrix is fine as long as it reduces to axis-aligned scale plus
          // translation. Rejecting matrix3d outright would suppress translateZ(0)
          // and translate3d(x,y,0) — the ubiquitous GPU-promotion hack — and cost
          // every iframe beneath such a container its real-click ripple.
          if (m.is2D === false) {
            if (Math.abs(m.m13) > 1e-6 || Math.abs(m.m23) > 1e-6) return false; // 3D rotation
            if (Math.abs(m.m31) > 1e-6 || Math.abs(m.m32) > 1e-6) return false;
            if (Math.abs(m.m14) > 1e-6 || Math.abs(m.m24) > 1e-6 || Math.abs(m.m34) > 1e-6) {
              return false;  // perspective
            }
          }
        } catch (e) {
          return false;  // unparseable transform — treat as unsafe
        }
      }
      // The individual transform properties do NOT appear in the transform
      // shorthand, so a rotate:20deg would otherwise read as none and slip through.
      var rot = cs.rotate;
      if (rot && rot !== 'none' && !/^0(deg|rad|grad|turn)?$/.test(String(rot).trim())) {
        return false;
      }
      var sc = cs.scale;
      if (sc && sc !== 'none' && String(sc).indexOf('-') >= 0) return false;
      node = node.parentElement;
    }
    return true;
  }

  function mapFromChild(win) {
    var frames = document.querySelectorAll('iframe,frame');
    for (var i = 0; i < frames.length; i++) {
      var el = frames[i];
      var cw = null;
      try { cw = el.contentWindow; } catch (e) { cw = null; }
      if (cw && cw === win) {
        if (!axisAligned(el)) return null;  // rotated/skewed: suppress, don't guess
        var r = el.getBoundingClientRect();
        // offsetWidth/Height are the UNTRANSFORMED border box, which is exactly
        // what getBoundingClientRect() measures after transform — so their ratio
        // is 1 for an ordinary iframe, borders and all. clientWidth excludes
        // borders, which made a plain 2px-bordered iframe scale by 404/400.
        var ow = el.offsetWidth || r.width || 1;
        var oh = el.offsetHeight || r.height || 1;
        var sx = r.width / ow;
        var sy = r.height / oh;
        // The child's origin sits inside the border, and the border is scaled too.
        return {
          x: r.left + (el.clientLeft || 0) * sx,
          y: r.top + (el.clientTop || 0) * sy,
          sx: sx,
          sy: sy,
        };
      }
    }
    return null;
  }

  // ── Child frames: forward input upward, never render ──
  if (!isTop) {
    var post = function (payload) {
      try { parent.postMessage(payload, '*'); } catch (e) { /* detached/blocked parent */ }
    };
    document.addEventListener('mousemove', function (e) {
      post({ k: MSG, type: 'move', x: e.clientX, y: e.clientY });
    }, { capture: true, passive: true });
    document.addEventListener('mousedown', function (e) {
      post({ k: MSG, type: 'down', x: e.clientX, y: e.clientY });
    }, { capture: true, passive: true });
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || !t.getBoundingClientRect) return;
      var r = t.getBoundingClientRect();
      post({ k: MSG, type: 'focus', x: r.left, y: r.top, w: r.width, h: r.height });
    }, true);
    // Relay grandchildren upward, mapping one hop at a time.
    window.addEventListener('message', function (ev) {
      var d = ev.data;
      if (!d || d.k !== MSG) return;
      var m = mapFromChild(ev.source);
      if (!m) return;
      post({
        k: MSG, type: d.type,
        x: d.x * m.sx + m.x, y: d.y * m.sy + m.y,
        w: typeof d.w === 'number' ? d.w * m.sx : d.w,
        h: typeof d.h === 'number' ? d.h * m.sy : d.h,
      });
    }, false);
    return;
  }

  // ── Top frame: the one and only renderer ──
  var root = null;         // the closed ShadowRoot — held ONLY in this closure
  var cursorEl = null;
  var generation = 0;      // bumped per run_code block; stale work becomes inert
  var transient = [];
  var activeMemo = null;   // set by cue()/endGeneration; survives origin changes
  var suppressReactiveUntil = 0;
  var lastX = 0, lastY = 0;
  var lastCueAt = 0, lastRippleAt = 0;
  var lastRippleX = 0, lastRippleY = 0;

  /**
   * Whether reactive listeners may animate. A driver cue is itself proof we are
   * inside a run_code block, so it latches this in memory — sessionStorage is
   * per-origin and throws outright on opaque origins, so it cannot be the only
   * source of truth once a block navigates.
   */
  function active() {
    if (activeMemo !== null) return activeMemo;
    try { return sessionStorage.getItem(ACTIVE_KEY) === '1'; } catch (e) { return false; }
  }

  function ensure() {
    if (root) return root;
    if (!document.body) return null;
    var host = document.createElement(HOST_TAG);
    host.setAttribute('aria-hidden', 'true');
    try { host.inert = true; } catch (e) { /* older engines: attribute below still applies */ }
    host.setAttribute('inert', '');
    host.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;margin:0;padding:0;border:0;' +
      'overflow:visible;pointer-events:none;z-index:2147483647;';
    try {
      root = host.attachShadow({ mode: 'closed' });
    } catch (e) {
      return null;
    }
    var style = document.createElement('style');
    style.textContent = CSS;
    root.appendChild(style);
    cursorEl = document.createElement('div');
    cursorEl.id = CURSOR_ID;
    cursorEl.innerHTML = SVG;
    cursorEl.style.cssText =
      'position:fixed;pointer-events:none;left:0px;top:0px;' +
      'transition-property:left,top;transition-timing-function:cubic-bezier(0.25,0.1,0.25,1);' +
      'transition-duration:' + DEFAULT_TRANSITION_MS + 'ms';
    root.appendChild(cursorEl);
    document.body.appendChild(host);
    try {
      var saved = JSON.parse(sessionStorage.getItem(POS_KEY) || 'null');
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
        lastX = saved.x; lastY = saved.y;
        cursorEl.style.left = lastX + 'px';
        cursorEl.style.top = lastY + 'px';
      }
    } catch (e) { /* sessionStorage unavailable — start at 0,0 */ }
    return root;
  }

  function track(node, gen, ttlMs) {
    var entry = { node: node, gen: gen, timer: 0 };
    entry.timer = setTimeout(function () { remove(entry); }, ttlMs);
    transient.push(entry);
    return entry;
  }

  function remove(entry) {
    var i = transient.indexOf(entry);
    if (i >= 0) transient.splice(i, 1);
    if (entry.timer) clearTimeout(entry.timer);
    if (entry.node && entry.node.parentNode) entry.node.parentNode.removeChild(entry.node);
  }

  function clearTransient() {
    var pending = transient.slice();
    for (var i = 0; i < pending.length; i++) remove(pending[i]);
    transient.length = 0;
  }

  function moveTo(x, y, durationMs) {
    var r = ensure();
    if (!r || !cursorEl) return;
    cursorEl.style.transitionDuration =
      (typeof durationMs === 'number' ? Math.max(0, durationMs) : DEFAULT_TRANSITION_MS) + 'ms';
    cursorEl.style.left = x + 'px';
    cursorEl.style.top = y + 'px';
    lastX = x; lastY = y;
    try { sessionStorage.setItem(POS_KEY, JSON.stringify({ x: x, y: y })); } catch (e) {}
  }

  function glideMsTo(x, y) {
    var dx = x - lastX, dy = y - lastY;
    var dist = Math.sqrt(dx * dx + dy * dy);
    return Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, Math.round(dist / GLIDE_PX_PER_MS)));
  }

  function box(cls, b, gen, ttlMs) {
    var r = ensure();
    if (!r) return;
    var d = document.createElement('div');
    d.className = 'chatsdet-highlight ' + cls;
    d.style.left = (b.x - 4) + 'px';
    d.style.top = (b.y - 4) + 'px';
    d.style.width = (b.width + 8) + 'px';
    d.style.height = (b.height + 8) + 'px';
    r.appendChild(d);
    track(d, gen, ttlMs);
  }

  function ripple(x, y, gen) {
    var r = ensure();
    if (!r) return;
    var d = document.createElement('div');
    d.className = 'chatsdet-highlight chatsdet-highlight--click';
    d.style.left = x + 'px';
    d.style.top = y + 'px';
    r.appendChild(d);
    track(d, gen, 600);
    lastRippleAt = Date.now();
    lastRippleX = x;
    lastRippleY = y;
  }

  function keycap(labels, gen) {
    var r = ensure();
    if (!r || !labels || !labels.length) return;
    var tray = document.createElement('div');
    tray.className = 'chatsdet-keycap-tray';
    for (var i = 0; i < labels.length; i++) {
      var cap = document.createElement('div');
      cap.className = 'chatsdet-keycap';
      cap.textContent = String(labels[i]);
      tray.appendChild(cap);
    }
    r.appendChild(tray);
    track(tray, gen, 900);
  }

  // ── Real input: the "it actually happened" signal ──
  // A driver cue renders INTENT (halo + glide). The ripple below is what says a
  // click really landed, so an action that later fails leaves no click artifact.
  function onMove(x, y) {
    if (!active() || Date.now() < suppressReactiveUntil) return;
    moveTo(x, y, DEFAULT_TRANSITION_MS);
  }
  function onDown(x, y) {
    if (!active()) return;
    moveTo(x, y, 0);
    ripple(x, y, generation);
  }
  function onFocus(b) {
    if (!active() || Date.now() < suppressReactiveUntil) return;
    box('chatsdet-highlight--type', b, generation, 600);
  }

  document.addEventListener('mousemove', function (e) { onMove(e.clientX, e.clientY); },
    { capture: true, passive: true });
  document.addEventListener('mousedown', function (e) { onDown(e.clientX, e.clientY); },
    { capture: true, passive: true });
  // reads only the target's bounding box — never .value or typed content.
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || !t.getBoundingClientRect) return;
    var r = t.getBoundingClientRect();
    onFocus({ x: r.left, y: r.top, width: r.width, height: r.height });
  }, true);

  // Input forwarded up from child frames, mapped into our viewport.
  window.addEventListener('message', function (ev) {
    var d = ev.data;
    if (!d || d.k !== MSG) return;
    var m = mapFromChild(ev.source);
    if (!m) return;  // not one of our frames — ignore forged messages
    if (!num(d.x) || !num(d.y)) return;
    var x = d.x * m.sx + m.x, y = d.y * m.sy + m.y;
    if (d.type === 'move') onMove(x, y);
    else if (d.type === 'down') onDown(x, y);
    else if (d.type === 'focus') {
      onFocus({ x: x, y: y, width: (d.w || 0) * m.sx, height: (d.h || 0) * m.sy });
    }
  }, false);

  var api = {
    beginGeneration: function (t, gen) {
      if (t !== TOKEN || !num(gen)) return;
      generation = gen;
      activeMemo = true;
      clearTransient();
      suppressReactiveUntil = 0;
    },
    endGeneration: function (t, gen) {
      if (t !== TOKEN || gen !== generation) return;
      activeMemo = false;
      clearTransient();
      suppressReactiveUntil = 0;
    },
    cue: function (t, req) {
      // Freezing the object stops the page REPLACING this function; the token is
      // what stops the page CALLING it. Without one, page script could advance
      // the generation (silently invalidating every real cue in the block) or
      // draw arbitrary overlay content. The token lives only in this closure and
      // in the driver — init-script source is not readable from the page.
      if (t !== TOKEN) return null;
      if (!req || !num(req.gen) || req.gen < generation) return null;
      // Reassert state BEFORE any rendering decision, so a cue that renders
      // nothing still re-arms the reactive fallback in a post-navigation
      // document (which starts with activeMemo = null and, across an origin
      // change, no sessionStorage flag either).
      generation = req.gen;
      activeMemo = true;
      var hold = num(req.holdMs) ? Math.max(0, Math.min(req.holdMs, 5000)) : 0;
      var wantsPoint = req.point && num(req.point.x) && num(req.point.y);
      var wantsBox = req.box && num(req.box.x) && num(req.box.y)
        && num(req.box.width) && num(req.box.height);
      var wantsKeys = req.keys && req.keys.length;
      // A pure state sync (syncState) must draw NOTHING. Returning before
      // ensure() matters: ensure() would install the host and, on a fresh
      // document with no saved position, park a visible arrow at (0,0) — an
      // artifact worse than the reactive-only behavior the sync exists to
      // preserve. The host is created later by the real input anyway, already
      // positioned correctly.
      if (!wantsPoint && !wantsBox && !wantsKeys) return { glideMs: 0 };
      if (!ensure()) return null;
      var glide = 0;
      if (wantsPoint) {
        glide = glideMsTo(req.point.x, req.point.y);
        moveTo(req.point.x, req.point.y, glide);
      }
      if (wantsBox) box('chatsdet-highlight--halo', req.box, req.gen, hold + glide + 200);
      if (wantsKeys) keycap(req.keys.slice(0, 8), req.gen);
      if (glide > 0 || hold > 0) {
        suppressReactiveUntil = Date.now() + glide + hold;
        lastCueAt = Date.now();
      }
      return { glideMs: glide };
    },
    focusBox: function (t) {
      if (t !== TOKEN) return null;
      if (!ensure()) return null;
      var el = document.activeElement;
      if (!el || el === document.body || el === document.documentElement) return null;
      if (el.tagName === 'IFRAME' || el.tagName === 'FRAME') return null;
      if (el.shadowRoot) return null;
      var rect = el.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return null;
      return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
    },
    /** Diagnostics: when the last cue rendered / last real click rippled. */
    lastCueAt: function () { return lastCueAt; },
    lastRippleAt: function () { return lastRippleAt; },
    /** Where the last real click rippled, in TOP-FRAME viewport coordinates. */
    lastRipplePoint: function () { return { x: lastRippleX, y: lastRippleY }; },
    hostPresent: function () { return Boolean(root); },
  };

  // Locked down: the driver sleeps for a duration this API returns, so a page
  // that could swap cue() could stall the run with an arbitrary glideMs.
  try { Object.freeze(api); } catch (e) {}
  try {
    Object.defineProperty(window, ${JSON.stringify(CUE_GLOBAL)}, {
      value: api, writable: false, configurable: false, enumerable: false,
    });
  } catch (e) {
    window[${JSON.stringify(CUE_GLOBAL)}] = api;
  }
})();`;
}
